import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { Beat } from "../../../plugins/spinners.plugin";
import { Dialog, Transition } from "@headlessui/react";
import Button from "../../buttons/buttons";
import { RiCloseLine } from "react-icons/ri";
import { ReactComponent as Upload } from "../../../assets/svgIcons/upload.svg";
import { storeAFoodAction } from "../../../redux/food/foodActions";
import { createAMerchandise } from "../../../redux/merchandise/merchandiseActions";
import { getOutletFromCookies, getUserFromStorage } from "../../../utils/Auth";
import { toast } from "react-toastify";
import { cleanInput } from "../../../helpers/cleanInput";
import FoodSampleCSV from "../../../assets/csv/Inventory Sample Sheet (Food).csv";
import MerchandiseSampleCSV from "../../../assets/csv/Inventory Sample Sheet (Merchandsise).csv";

function parseCSV(text) {
  const lines = text.split("\n").filter((line) => line.trim() !== "");
  if (lines.length < 2) return [];
  const headers = parseCSVLine(lines[0]);
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    const row = {};
    headers.forEach((header, index) => {
      row[header.trim()] = values[index]?.trim() || "";
    });
    rows.push(row);
  }
  return rows;
}

function parseCSVLine(line) {
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ",") {
        result.push(current);
        current = "";
      } else {
        current += char;
      }
    }
  }
  result.push(current);
  return result;
}

function parseJSONField(value) {
  try {
    return JSON.parse(value);
  } catch {
    return value ? [value] : [];
  }
}

function buildFoodPayload(row, business, outletId) {
  return {
    business_id: business,
    restaurant_id: outletId,
    food_name: row.food_name,
    food_description: row.food_description || "",
    food_categories: parseJSONField(row.food_categories),
    food_tags: parseJSONField(row.food_tags),
    food_attributes: parseJSONField(row.food_attributes),
    food_amount: row.food_amount,
    food_discount: row.food_discount || 0,
    food_preparation_time: row.food_preparation_time || "",
    food_operating_status: row.food_operating_status || "available",
    food_pictures: parseJSONField(row.food_pictures),
    food_weight_in_kg: row.food_weight_in_kg || "",
  };
}

function buildMerchandisePayload(row, business, outletId) {
  return {
    business_id: business,
    store_id: outletId,
    merchandise_name: row.merchandise_name,
    merchandise_description: row.merchandise_description || "",
    merchandise_categories: parseJSONField(row.merchandise_categories),
    merchandise_tags: parseJSONField(row.merchandise_tags),
    merchandise_attributes: parseJSONField(row.merchandise_attributes),
    merchandise_amount: row.merchandise_amount,
    merchandise_discount: row.merchandise_discount || 0,
    merchandise_preparation_time: row.merchandise_preparation_time || "",
    merchandise_operating_status:
      row.merchandise_operating_status || "available",
    merchandise_pictures: parseJSONField(row.merchandise_pictures),
    merchandise_weight_in_kg: row.merchandise_weight_in_kg || "",
  };
}

const CONFIG = {
  food: {
    sampleCSV: FoodSampleCSV,
    sampleFileName: "Inventory Sample Sheet (Food).csv",
    action: storeAFoodAction,
    buildPayload: buildFoodPayload,
    label: "food",
  },
  merchandise: {
    sampleCSV: MerchandiseSampleCSV,
    sampleFileName: "Inventory Sample Sheet (Merchandise).csv",
    action: createAMerchandise,
    buildPayload: buildMerchandisePayload,
    label: "merchandise",
  },
};

export default function ImportInventoryModal({ show, onClose, type = "food" }) {
  const dispatch = useDispatch();
  const outlet = getOutletFromCookies();
  const user = getUserFromStorage();
  const business = Object.keys(user?.groups || {})[0];
  const fileInputRef = useRef(null);
  const config = CONFIG[type] || CONFIG.food;

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (selected && selected.type === "text/csv") {
      setFile(selected);
    } else if (selected) {
      toast.error("Only CSV files are allowed");
      e.target.value = "";
    }
  };

  const handleDownloadSample = () => {
    const link = document.createElement("a");
    link.href = config.sampleCSV;
    link.download = config.sampleFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a CSV file first");
      return;
    }

    const text = await file.text();
    const rows = parseCSV(text);

    if (rows.length === 0) {
      toast.error("CSV file is empty or invalid");
      return;
    }

    setUploading(true);
    setProgress({ current: 0, total: rows.length });
    let successCount = 0;

    for (let i = 0; i < rows.length; i++) {
      const payload = config.buildPayload(rows[i], business, outlet?.id);
      cleanInput(payload);
      try {
        const response = await dispatch(config.action(payload));
        if (response?.status === "success") {
          successCount++;
        }
      } catch (error) {
        console.error(`Failed to upload row ${i + 1}:`, error);
      }
      setProgress({ current: i + 1, total: rows.length });
    }

    setUploading(false);
    toast.success(
      `${successCount} of ${rows.length} ${config.label} items imported`
    );
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  };

  const handleClose = () => {
    if (!uploading) {
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      onClose();
    }
  };

  return (
    <Transition appear show={show}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-full p-4 text-center sm:p-0">
            <Transition.Child
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="bg-white dark:bg-[#121212] max-w-[650px] w-full sm:rounded-md rounded-sm p-4 sm:p-6 sm:py-12">
                <div>
                  <div className="hidden sm:block absolute top-3 right-2 pt-4 pr-4">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={uploading}
                    >
                      <span className="sr-only">Close</span>
                      <RiCloseLine />
                    </button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="text-center border border-dashed border-primary rounded-lg p-4 flex flex-col items-center justify-center">
                      {uploading ? (
                        <div className="mt-4 w-full">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full transition-all"
                              style={{
                                width: `${
                                  (progress.current / progress.total) * 100
                                }%`,
                              }}
                            />
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            {progress.current} of {progress.total} items
                          </p>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-20 h-20 mb-4" />
                          <input
                            type="file"
                            accept=".csv"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            className="hidden"
                            id="csv-file-input"
                          />
                          <label
                            htmlFor="csv-file-input"
                            className="cursor-pointer"
                          >
                            <h2 className="text-base font-medium">
                              {file ? file.name : "Upload Files"}
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">
                              {file
                                ? "Click to change file"
                                : "Only CSV files are allowed"}
                            </p>
                          </label>
                        </>
                      )}
                    </div>
                    <div>
                      <h2 className="text-lg sm:text-xl font-medium text-black dark:text-white">
                        Upload Inventory
                      </h2>
                      {uploading ? (
                        <p className="text-base text-gray-500 mt-1">
                          Add your inventory to the system as a CSV file,
                          download a sample to see required format.
                        </p>
                      ) : (
                        <p>Please don’t close until the upload is complete.</p>
                      )}

                      <div className="flex justify-between mt-14 sm:mt-16">
                        <Button.Secondary
                          title={"Download Sample"}
                          className={"rounded-md px-4"}
                          onClick={handleDownloadSample}
                        />
                        <Button.Primary
                          title={uploading ? <Beat /> : "Continue"}
                          className={"rounded-md px-4"}
                          type="button"
                          disabled={uploading || !file}
                          onClick={handleUpload}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
